import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelL } from '@sorare/core/src/atoms/typography';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { RevealPackCardsDialogFromReward } from 'components/commonCard/RevealPackCardsDialogFromReward';

import type {
  DailyClaimModule_task,
  NewMarketClaimDailyPackMutation,
  NewMarketClaimDailyPackMutationVariables,
} from './__generated__/index.graphql';

const Root = styled.div`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  background: var(--c-nl-500);
  backdrop-filter: blur(var(--unit));
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--double-unit);
  overflow: hidden;
`;
const SpaceBetweenVertical = styled(Vertical)`
  gap: var(--double-unit);
  justify-content: space-between;
`;
const PackImgWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  justify-self: center;
  @media ${laptopAndAbove} {
    width: calc(15 * var(--unit));
    aspect-ratio: var(--pack-aspect-ratio);
  }
`;

const StyledBadge = styled(Badge)`
  width: 100%;
  position: absolute;
  left: 0;
  right: 0;
  top: var(--double-unit);
`;
const PackImg = styled.img`
  width: 100%;
  object-fit: contain;
  aspect-ratio: var(--pack-aspect-ratio);
`;
const NEW_MARKET_CLAIM_DAILY_PACK_MUTATION = gql`
  mutation NewMarketClaimDailyPackMutation($taskId: ID!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
      }
      rewards {
        id
        ...RevealPackCardsDialogFromReward_anyRewardInterface
      }
      errors {
        message
        code
      }
    }
  }
  ${RevealPackCardsDialogFromReward.fragments.anyRewardInterface}
` as TypedDocumentNode<
  NewMarketClaimDailyPackMutation,
  NewMarketClaimDailyPackMutationVariables
>;

type Props = {
  task: DailyClaimModule_task;
  refetch: () => Promise<unknown>;
};
export const DailyClaimModule = ({ task, refetch }: Props) => {
  const { isIosApp, isAndroidApp, postMessage } = useIsMobileApp();
  const {
    flags: { disableNativeIOSPackOpening = false },
  } = useFeatureFlags();
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();
  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [claimMutationData, setClaimMutationData] =
    useState<NewMarketClaimDailyPackMutation['claimTask']>();
  const [claimTask] = useMutation(NEW_MARKET_CLAIM_DAILY_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const isClaimable = task.aasmState !== ManagerProgressionTaskState.CLAIMED;
  const packPictureUrl = task.rewardConfigs.find(
    isTypeFn('CardPacksRewardConfig')
  )?.cardPack?.pictureUrl;

  const onClaim = async () => {
    if (!isClaimable) {
      return;
    }
    if (isIosApp && !disableNativeIOSPackOpening) {
      // on ios the message opens the native pack opening dialog
      postMessage('claimFreeCardPack', {
        taskId: task.id,
      });
      return;
    }
    setPackDialogOpen(true);
    const { data } = await claimTask({
      variables: {
        taskId: task.id,
      },
    });
    if (isAndroidApp) {
      // on android the message refreshes the market red dot, pack opening is still handled by the web
      postMessage('claimFreeCardPack', {
        taskId: task.id,
      });
    }
    setClaimMutationData(data?.claimTask);
  };

  const reward = claimMutationData?.rewards?.at(0);

  return (
    <>
      <Root>
        <SpaceBetweenVertical>
          <Vertical gap={0.5}>
            <BrandM>
              <FormattedMessage
                id="DailyClaimModule.dailyPack"
                defaultMessage="Daily Pack"
              />
            </BrandM>
            {!!isClaimable && (
              <LabelL
                className="text-gradient-color"
                style={
                  {
                    '--text-gradient-color':
                      'var(--c-gradient-conversionCreditBorder)',
                  } as CSSProperties
                }
              >
                <FormattedMessage {...glossary.freeClaimAvailable} />
              </LabelL>
            )}
          </Vertical>
          {isClaimable ? (
            <Button color="primary" size="medium" onClick={onClaim}>
              <FormattedMessage
                id="DailyClaimModule.button"
                defaultMessage="Claim Now"
              />
            </Button>
          ) : (
            <LabelL color="var(--c-nd-600)">
              {!!dailyTasksExpiresAt && (
                <FormattedMessage
                  {...glossary.freeClaimIn}
                  values={{
                    value: (
                      <TimeLeft time={dailyTasksExpiresAt} onEnded={refetch} />
                    ),
                  }}
                />
              )}
            </LabelL>
          )}
        </SpaceBetweenVertical>
        <PackImgWrapper>
          <StyledBadge badgeContent="1" invisible={!isClaimable} size="medium">
            {!!packPictureUrl && (
              <PackImg
                src={proxyUrl(packPictureUrl, { cropWidth: 320 })}
                alt=""
              />
            )}
          </StyledBadge>
        </PackImgWrapper>
      </Root>

      {!!reward && (
        <RevealPackCardsDialogFromReward
          open={packDialogOpen}
          reward={claimMutationData?.rewards?.at(0)}
          onClose={() => {
            setPackDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

DailyClaimModule.fragments = {
  task: gql`
    fragment DailyClaimModule_task on Task {
      id
      aasmState
      rewardConfigs {
        id
        ... on CardPacksRewardConfig {
          id
          cardPack {
            slug
            pictureUrl
          }
        }
      }
    }
  ` as TypedDocumentNode<DailyClaimModule_task>,
};
